import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import BadgeList from '../../components/BadgeList';
import { determineUserBadges } from '../../utils/badgeUtils';
import { useAuth } from '../../contexts/AuthContext';
import { fixAdminUserProfile } from '../../services/firestoreService';
import 'react-tooltip/dist/react-tooltip.css';

const Profile = () => {
  const { userProfile, currentUser, loading, updateUserProfile } = useAuth();

  // Initialize userData from Firebase profile
  const [userData, setUserData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    secondaryEmail: '',
    title: '',
    roleDescription: '',
    company: '',
    companyCategory: '',
    companyDescription: '',
    city: '',
    state: '',
    workPhone: '',
    cellPhone: '',
    memberTier: '',
    memberSince: '',
    chapter: '',
    role: '',
    roleStartDate: '',
    isFoundingMember: false,
    isVipMember: false,
    bio: '',
    linkedIn: '',
    profilePhoto: '',
    groups: [],
    eventsRegistered: [],
    showEvents: true,
    network: [],
    education: [],
    experience: []
  });

  // State for edit mode
  const [isEditMode, setIsEditMode] = useState(false);
  const [editedUserData, setEditedUserData] = useState(userData);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [userBadges, setUserBadges] = useState([]);
  const [fixingProfile, setFixingProfile] = useState(false);

  // Load user data from Firebase when userProfile changes
  useEffect(() => {
    if (userProfile) {
      setUserData({
        firstName: userProfile.firstName || '',
        lastName: userProfile.lastName || '',
        email: userProfile.email || '',
        secondaryEmail: userProfile.secondaryEmail || '',
        title: userProfile.jobTitle || '',
        roleDescription: userProfile.roleDescription || '',
        company: userProfile.company || '',
        companyCategory: userProfile.companyCategory || '',
        companyDescription: userProfile.companyDescription || '',
        city: userProfile.city || '',
        state: userProfile.state || '',
        workPhone: userProfile.workPhone || userProfile.phone || '',
        cellPhone: userProfile.cellPhone || '',
        memberTier: userProfile.membershipTier || '',
        memberSince: userProfile.memberSince ? new Date(userProfile.memberSince.seconds * 1000).toLocaleDateString() : '',
        chapter: userProfile.chapter || '',
        role: userProfile.role || '',
        roleStartDate: userProfile.roleStartDate || '',
        isFoundingMember: userProfile.isFoundingMember || false,
        isVipMember: userProfile.isVipMember || true, // Temporarily set to true for testing
        referralCount: userProfile.referralCount || 6, // Temporarily set for Ambassador badge
        bio: userProfile.aboutMe || '',
        linkedIn: userProfile.linkedinUrl || '',
        profilePhoto: userProfile.profilePhoto || '',
        groups: userProfile.groups || [],
        eventsRegistered: userProfile.eventsRegistered || [],
        showEvents: userProfile.showEvents !== false,
        network: userProfile.network || [],
        education: userProfile.education || [],
        experience: userProfile.experience || []
      });
    }
  }, [userProfile]);

  // Determine user badges when userData changes
  useEffect(() => {
    const badges = determineUserBadges(userData);
    setUserBadges(badges);
  }, [userData]);

  // Fix admin profile function
  const handleFixAdminProfile = async () => {
    setFixingProfile(true);
    try {
      const success = await fixAdminUserProfile();
      if (success) {
        alert('Admin profile updated! Please refresh the page to see changes.');
        window.location.reload();
      } else {
        alert('Failed to update admin profile. Check console for errors.');
      }
    } catch (error) {
      console.error('Error fixing admin profile:', error);
      alert('Error updating admin profile. Check console for details.');
    } finally {
      setFixingProfile(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-apn-blue"></div>
      </div>
    );
  }

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value, type, files } = e.target;

    if (type === 'file' && files && files[0]) {
      // Handle file upload
      const file = files[0];

      // Create a preview URL for the image
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result);
        setEditedUserData(prevState => ({
          ...prevState,
          profilePhoto: reader.result
        }));
      };
      reader.readAsDataURL(file);
    } else {
      // Handle other input types
      setEditedUserData(prevState => ({
        ...prevState,
        [name]: value
      }));
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      // Update Firebase with the edited data
      const updates = {
        firstName: editedUserData.firstName,
        lastName: editedUserData.lastName,
        email: editedUserData.email,
        secondaryEmail: editedUserData.secondaryEmail,
        jobTitle: editedUserData.title,
        roleDescription: editedUserData.roleDescription,
        company: editedUserData.company,
        companyCategory: editedUserData.companyCategory,
        companyDescription: editedUserData.companyDescription,
        city: editedUserData.city,
        state: editedUserData.state,
        phone: editedUserData.workPhone,
        cellPhone: editedUserData.cellPhone,
        chapter: editedUserData.chapter,
        aboutMe: editedUserData.bio,
        linkedinUrl: editedUserData.linkedIn,
        profilePhoto: editedUserData.profilePhoto,
        groups: editedUserData.groups,
        eventsRegistered: editedUserData.eventsRegistered,
        showEvents: editedUserData.showEvents,
        education: editedUserData.education,
        experience: editedUserData.experience
      };

      // Use the updateUserProfile function from AuthContext
      await updateUserProfile(currentUser.uid, updates);

      setUserData(editedUserData);
      setIsEditMode(false);

      // Show success message (you could add a toast notification here)
      alert('Profile updated successfully!');
    } catch (error) {
      console.error('Error updating profile:', error);
      alert('Error updating profile. Please try again.');
    }
  };

  return (
    <div className="bg-white">
      {/* Page Header */}
      <div className="bg-apn-blue text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            {/* APN Logo */}
            <div className="flex justify-center mb-4">
              <img
                src={`${process.env.PUBLIC_URL}/apn-logos/APN-Tag-white.jpg`}
                alt="APN Logo"
                className="h-16"
              />
            </div>
            <h1 className="text-3xl font-bold">My Profile</h1>
          </div>
        </div>
      </div>

      {/* Profile Content */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-lg shadow-lg overflow-hidden">
            {/* Profile Header */}
            <div className="bg-gray-50 p-6 flex flex-col md:flex-row justify-between items-center border-b border-gray-200">
              <div className="flex items-center mb-4 md:mb-0">
                <div className="w-24 h-24 rounded-full mr-6 flex-shrink-0 overflow-hidden">
                  {userData.profilePhoto ? (
                    <img
                      src={userData.profilePhoto}
                      alt="Profile"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="h-full w-full bg-gray-300 flex items-center justify-center">
                      <span className="text-gray-600 text-xl">{userData.firstName.charAt(0)}{userData.lastName.charAt(0)}</span>
                    </div>
                  )}
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h2 className="text-2xl font-bold text-gray-900">{userData.firstName} {userData.lastName}</h2>
                    <BadgeList badges={userBadges} size="small" className="ml-2" />
                  </div>
                  <p className="text-gray-600">{userData.title} at {userData.company}</p>
                  <p className="text-gray-600">{userData.city}, {userData.state}</p>
                  <p className="text-gray-600">Member Since: {userData.memberSince}</p>
                  {userData.role && <p className="text-gray-600">Role: {userData.role}</p>}
                </div>
              </div>
              <div>
                {isEditMode ? (
                  <div className="flex space-x-4">
                    <button
                      onClick={() => setIsEditMode(false)}
                      className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSubmit}
                      className="px-4 py-2 bg-apn-blue text-white rounded-md hover:bg-blue-700"
                    >
                      Save Changes
                    </button>
                  </div>
                ) : (
                  <div className="flex space-x-4">
                    <button
                      onClick={() => {
                        setEditedUserData(userData);
                        setIsEditMode(true);
                      }}
                      className="px-4 py-2 bg-apn-blue text-white rounded-md hover:bg-blue-700"
                    >
                      Edit Profile
                    </button>
                    {/* Admin Fix Button - Only show for admin users */}
                    {userData.email === 'admin@the-apn.com' && (
                      <button
                        onClick={handleFixAdminProfile}
                        disabled={fixingProfile}
                        className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50"
                      >
                        {fixingProfile ? 'Fixing...' : 'Fix Admin Profile'}
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Profile Content */}
            <div className="p-6">
              {isEditMode ? (
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Profile Photo */}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Profile Photo</h3>
                    <div className="flex items-center space-x-6 mb-6">
                      <div className="shrink-0">
                        {photoPreview ? (
                          <img
                            className="h-24 w-24 object-cover rounded-full"
                            src={photoPreview}
                            alt="Profile preview"
                          />
                        ) : editedUserData.profilePhoto ? (
                          <img
                            className="h-24 w-24 object-cover rounded-full"
                            src={editedUserData.profilePhoto}
                            alt="Current profile"
                          />
                        ) : (
                          <div className="h-24 w-24 rounded-full bg-gray-200 flex items-center justify-center">
                            <span className="text-gray-600 text-xl">{editedUserData.firstName.charAt(0)}{editedUserData.lastName.charAt(0)}</span>
                          </div>
                        )}
                      </div>
                      <div className="flex text-sm text-gray-600">
                        <label htmlFor="profilePhoto" className="relative cursor-pointer bg-white rounded-md font-medium text-apn-blue hover:text-blue-700 focus-within:outline-none">
                          <span>Change photo</span>
                          <input
                            id="profilePhoto"
                            name="profilePhoto"
                            type="file"
                            accept="image/*"
                            className="sr-only"
                            onChange={handleInputChange}
                          />
                        </label>
                        <p className="pl-1">or drag and drop</p>
                      </div>
                    </div>
                  </div>

                  {/* Personal Information */}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Personal Information</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-1">
                          First Name *
                        </label>
                        <input
                          type="text"
                          id="firstName"
                          name="firstName"
                          value={editedUserData.firstName}
                          onChange={handleInputChange}
                          required
                          className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-apn-blue"
                        />
                      </div>
                      <div>
                        <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-1">
                          Last Name *
                        </label>
                        <input
                          type="text"
                          id="lastName"
                          name="lastName"
                          value={editedUserData.lastName}
                          onChange={handleInputChange}
                          required
                          className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-apn-blue"
                        />
                      </div>
                      <div>
                        <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                          Email *
                        </label>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          value={editedUserData.email}
                          onChange={handleInputChange}
                          required
                          className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-apn-blue"
                        />
                      </div>
                      <div>
                        <label htmlFor="secondaryEmail" className="block text-sm font-medium text-gray-700 mb-1">
                          Secondary Email
                        </label>
                        <input
                          type="email"
                          id="secondaryEmail"
                          name="secondaryEmail"
                          value={editedUserData.secondaryEmail}
                          onChange={handleInputChange}
                          className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-apn-blue"
                        />
                      </div>
                      <div>
                        <label htmlFor="workPhone" className="block text-sm font-medium text-gray-700 mb-1">
                          Work Phone *
                        </label>
                        <input
                          type="text"
                          id="workPhone"
                          name="workPhone"
                          value={editedUserData.workPhone}
                          onChange={handleInputChange}
                          required
                          className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-apn-blue"
                        />
                      </div>
                      <div>
                        <label htmlFor="cellPhone" className="block text-sm font-medium text-gray-700 mb-1">
                          Cell Phone
                        </label>
                        <input
                          type="text"
                          id="cellPhone"
                          name="cellPhone"
                          value={editedUserData.cellPhone}
                          onChange={handleInputChange}
                          className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-apn-blue"
                        />
                      </div>
                      <div>
                        <label htmlFor="city" className="block text-sm font-medium text-gray-700 mb-1">
                          City *
                        </label>
                        <input
                          type="text"
                          id="city"
                          name="city"
                          value={editedUserData.city}
                          onChange={handleInputChange}
                          required
                          className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-apn-blue"
                        />
                      </div>
                      <div>
                        <label htmlFor="state" className="block text-sm font-medium text-gray-700 mb-1">
                          State *
                        </label>
                        <input
                          type="text"
                          id="state"
                          name="state"
                          value={editedUserData.state}
                          onChange={handleInputChange}
                          required
                          className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-apn-blue"
                        />
                      </div>
                      <div>
                        <label htmlFor="linkedIn" className="block text-sm font-medium text-gray-700 mb-1">
                          LinkedIn URL
                        </label>
                        <input
                          type="text"
                          id="linkedIn"
                          name="linkedIn"
                          value={editedUserData.linkedIn}
                          onChange={handleInputChange}
                          className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-apn-blue"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Company Information */}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Company Information</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label htmlFor="company" className="block text-sm font-medium text-gray-700 mb-1">
                          Company *
                        </label>
                        <input
                          type="text"
                          id="company"
                          name="company"
                          value={editedUserData.company}
                          onChange={handleInputChange}
                          required
                          className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-apn-blue"
                        />
                      </div>
                      <div>
                        <label htmlFor="companyCategory" className="block text-sm font-medium text-gray-700 mb-1">
                          Company Category *
                        </label>
                        <select
                          id="companyCategory"
                          name="companyCategory"
                          value={editedUserData.companyCategory}
                          onChange={handleInputChange}
                          required
                          className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-apn-blue"
                        >
                          <option value="">Select a category</option>
                          <option value="PE">PE</option>
                          <option value="IB">IB</option>
                          <option value="Wealth Management">Wealth Management</option>
                          <option value="Accounting">Accounting</option>
                          <option value="Legal">Legal</option>
                          <option value="Insurance">Insurance</option>
                          <option value="Recruiting">Recruiting</option>
                          <option value="Management Consulting">Management Consulting</option>
                          <option value="Individual">Individual</option>
                          <option value="Business Advisory">Business Advisory</option>
                          <option value="Other">Other</option>
                        </select>
                      </div>
                      <div>
                        <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">
                          Job Title *
                        </label>
                        <input
                          type="text"
                          id="title"
                          name="title"
                          value={editedUserData.title}
                          onChange={handleInputChange}
                          required
                          className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-apn-blue"
                        />
                      </div>
                      <div>
                        <label htmlFor="roleDescription" className="block text-sm font-medium text-gray-700 mb-1">
                          Role Description
                        </label>
                        <input
                          type="text"
                          id="roleDescription"
                          name="roleDescription"
                          value={editedUserData.roleDescription}
                          onChange={handleInputChange}
                          className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-apn-blue"
                        />
                      </div>
                      <div className="md:col-span-2">
                        <label htmlFor="companyDescription" className="block text-sm font-medium text-gray-700 mb-1">
                          Company Description
                        </label>
                        <textarea
                          id="companyDescription"
                          name="companyDescription"
                          rows="3"
                          value={editedUserData.companyDescription}
                          onChange={handleInputChange}
                          className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-apn-blue"
                        ></textarea>
                      </div>
                    </div>
                  </div>

                  {/* Membership Information */}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Membership Information</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label htmlFor="memberTier" className="block text-sm font-medium text-gray-700 mb-1">
                          Member Tier *
                        </label>
                        <select
                          id="memberTier"
                          name="memberTier"
                          value={editedUserData.memberTier}
                          onChange={handleInputChange}
                          required
                          className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-apn-blue"
                        >
                          <option value="">Select a tier</option>
                          <option value="Tier 1">Tier 1</option>
                          <option value="Tier 2">Tier 2</option>
                          <option value="Tier 3">Tier 3</option>
                        </select>
                      </div>
                      <div>
                        <label htmlFor="memberSince" className="block text-sm font-medium text-gray-700 mb-1">
                          Member Since *
                        </label>
                        <input
                          type="text"
                          id="memberSince"
                          name="memberSince"
                          value={editedUserData.memberSince}
                          onChange={handleInputChange}
                          required
                          disabled
                          className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-apn-blue bg-gray-100"
                        />
                      </div>
                      <div>
                        <label htmlFor="chapter" className="block text-sm font-medium text-gray-700 mb-1">
                          Chapter *
                        </label>
                        <input
                          type="text"
                          id="chapter"
                          name="chapter"
                          value={editedUserData.chapter}
                          onChange={handleInputChange}
                          required
                          className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-apn-blue"
                        />
                      </div>
                      <div>
                        <label className="flex items-center mt-6">
                          <input
                            type="checkbox"
                            name="showEvents"
                            checked={editedUserData.showEvents}
                            onChange={(e) => {
                              setEditedUserData(prevState => ({
                                ...prevState,
                                showEvents: e.target.checked
                              }));
                            }}
                            className="h-4 w-4 text-apn-blue focus:ring-apn-blue border-gray-300 rounded"
                          />
                          <span className="ml-2 text-sm text-gray-700">Display registered events on profile</span>
                        </label>
                      </div>
                    </div>
                  </div>

                  {/* Bio */}
                  <div className="mx-auto max-w-4xl">
                    <label htmlFor="bio" className="block text-sm font-medium text-gray-700 mb-1">
                      Professional Bio
                    </label>
                    <textarea
                      id="bio"
                      name="bio"
                      rows="6"
                      value={editedUserData.bio}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-apn-blue text-lg"
                      placeholder="Tell others about your professional background, interests, and expertise..."
                    ></textarea>
                  </div>


                </form>
              ) : (
                <div className="space-y-8">
                  {/* About */}
                  <div className="mx-auto max-w-4xl">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">About</h3>
                    <p className="text-gray-600 text-lg leading-relaxed">{userData.bio}</p>
                  </div>

                  {/* Contact Information */}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Contact Information</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <p className="text-gray-500 text-sm">Email</p>
                        <p className="text-gray-900">{userData.email}</p>
                      </div>
                      <div>
                        <p className="text-gray-500 text-sm">Secondary Email</p>
                        <p className="text-gray-900">{userData.secondaryEmail || 'Not provided'}</p>
                      </div>
                      <div>
                        <p className="text-gray-500 text-sm">Work Phone</p>
                        <p className="text-gray-900">{userData.workPhone}</p>
                      </div>
                      <div>
                        <p className="text-gray-500 text-sm">Cell Phone</p>
                        <p className="text-gray-900">{userData.cellPhone || 'Not provided'}</p>
                      </div>
                      <div>
                        <p className="text-gray-500 text-sm">LinkedIn</p>
                        <a href={userData.linkedIn} target="_blank" rel="noopener noreferrer" className="text-apn-blue hover:underline">
                          {userData.linkedIn}
                        </a>
                      </div>
                      <div>
                        <p className="text-gray-500 text-sm">Location</p>
                        <p className="text-gray-900">{userData.city}, {userData.state}</p>
                      </div>
                    </div>
                  </div>

                  {/* Company Information */}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Company Information</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <p className="text-gray-500 text-sm">Company</p>
                        <p className="text-gray-900">{userData.company}</p>
                      </div>
                      <div>
                        <p className="text-gray-500 text-sm">Company Category</p>
                        <p className="text-gray-900">{userData.companyCategory}</p>
                      </div>
                      <div>
                        <p className="text-gray-500 text-sm">Job Title</p>
                        <p className="text-gray-900">{userData.title}</p>
                      </div>
                      <div>
                        <p className="text-gray-500 text-sm">Role Description</p>
                        <p className="text-gray-900">{userData.roleDescription || 'Not provided'}</p>
                      </div>
                      <div className="md:col-span-2">
                        <p className="text-gray-500 text-sm">Company Description</p>
                        <p className="text-gray-900">{userData.companyDescription || 'Not provided'}</p>
                      </div>
                    </div>
                  </div>

                  {/* Membership Information */}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Membership Information</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <p className="text-gray-500 text-sm">Member Tier</p>
                        <p className="text-gray-900">{userData.memberTier}</p>
                      </div>
                      <div>
                        <p className="text-gray-500 text-sm">Member Since</p>
                        <p className="text-gray-900">{userData.memberSince}</p>
                      </div>
                      <div>
                        <p className="text-gray-500 text-sm">Chapter</p>
                        <p className="text-gray-900">{userData.chapter}</p>
                      </div>
                    </div>
                  </div>

                  {/* Badges */}
                  {userBadges.length > 0 && (
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">Badges & Achievements</h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
                        {userBadges.map((badge) => (
                          <div key={badge.id} className="flex items-center p-4 border border-gray-200 rounded-lg">
                            <img
                              src={`${process.env.PUBLIC_URL}${badge.image}`}
                              alt={badge.name}
                              className="w-16 h-16 object-contain mr-4"
                            />
                            <div>
                              <h4 className="font-semibold text-gray-900">{badge.name}</h4>
                              <p className="text-sm text-gray-600">{badge.publicDescription || badge.name}</p>
                              {badge.earnedDate && (
                                <p className="text-xs text-gray-500 mt-1">Earned: {badge.earnedDate}</p>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Experience */}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Experience</h3>
                    <div className="space-y-6">
                      {userData.experience.map(exp => (
                        <div key={exp.id} className="border-b border-gray-200 pb-6 last:border-b-0 last:pb-0">
                          <h4 className="font-semibold text-gray-900">{exp.title}</h4>
                          <p className="text-gray-600">{exp.company}</p>
                          <p className="text-gray-500 text-sm">{exp.startDate} - {exp.endDate}</p>
                          <p className="text-gray-600 mt-2">{exp.description}</p>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Education */}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Education</h3>
                    <div className="space-y-6">
                      {userData.education.map(edu => (
                        <div key={edu.id} className="border-b border-gray-200 pb-6 last:border-b-0 last:pb-0">
                          <h4 className="font-semibold text-gray-900">{edu.institution}</h4>
                          <p className="text-gray-600">{edu.degree}</p>
                          <p className="text-gray-500 text-sm">{edu.year}</p>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Groups */}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Groups</h3>
                    <div className="flex flex-wrap gap-2">
                      {userData.groups.length > 0 ? userData.groups.map((group, index) => (
                        <span key={index} className="inline-block bg-gray-100 text-gray-700 px-3 py-1 rounded-full">
                          {group}
                        </span>
                      )) : <p className="text-gray-600">Not a member of any groups yet</p>}
                    </div>
                  </div>

                  {/* Events */}
                  {userData.showEvents && (
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">Registered Events</h3>
                      <div className="flex flex-wrap gap-2">
                        {userData.eventsRegistered.length > 0 ? userData.eventsRegistered.map((event, index) => (
                          <span key={index} className="inline-block bg-gray-100 text-gray-700 px-3 py-1 rounded-full">
                            {event}
                          </span>
                        )) : <p className="text-gray-600">Not registered for any events yet</p>}
                      </div>
                    </div>
                  )}

                  {/* Network */}
                  <div>
                    <div className="flex justify-between items-center mb-4">
                      <h3 className="text-lg font-semibold text-gray-900">My Network (103)</h3>
                      <Link
                        to="/my-network"
                        className="text-apn-blue hover:text-blue-700 text-sm font-medium flex items-center"
                      >
                        View All
                        <svg className="ml-1 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                        </svg>
                      </Link>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {userData.network.length > 0 ? userData.network.slice(0, 6).map((connection) => (
                        <div key={connection.id} className="border border-gray-200 rounded-lg p-4 flex items-center">
                          <div className="flex-shrink-0 mr-4">
                            <img
                              src={`${process.env.PUBLIC_URL}${connection.image}`}
                              alt={connection.name}
                              className="h-12 w-12 rounded-full object-cover"
                              onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = `${process.env.PUBLIC_URL}/images/profiles/default-avatar.png`;
                              }}
                            />
                          </div>
                          <div className="flex-grow">
                            <h4 className="font-semibold text-gray-900">{connection.name}</h4>
                            <p className="text-sm text-gray-600">{connection.title} at {connection.company}</p>
                          </div>
                          <div>
                            <Link
                              to={`/directory/${connection.id}`}
                              className="inline-flex items-center px-3 py-1 border border-gray-300 text-sm leading-5 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:border-blue-300 focus:shadow-outline-blue active:bg-gray-100 transition ease-in-out duration-150"
                            >
                              View
                            </Link>
                          </div>
                        </div>
                      )) : <p className="text-gray-600">You haven't connected with anyone yet</p>}
                      {userData.network.length > 6 && (
                        <Link
                          to="/my-network"
                          className="border border-dashed border-gray-300 rounded-lg p-4 flex flex-col items-center justify-center text-gray-500 hover:text-apn-blue hover:border-apn-blue transition duration-200"
                        >
                          <span className="text-lg mb-1">+{userData.network.length - 6}</span>
                          <span>View All Connections</span>
                        </Link>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="bg-apn-blue text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          {/* APN Logo */}
          <div className="flex justify-center mb-6">
            <img
              src={`${process.env.PUBLIC_URL}/apn-logos/APN-Tag-SQ-white.jpg`}
              alt="APN Logo Square"
              className="h-20"
            />
          </div>
          <h2 className="text-3xl font-bold mb-4">Explore the APN Community</h2>
          <p className="text-xl mb-8 max-w-3xl mx-auto">
            Connect with other finance professionals, join groups, and discover job opportunities.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/directory" className="btn-primary bg-white text-apn-blue hover:bg-gray-100">
              Member Directory
            </Link>
            <Link to="/groups" className="btn-primary bg-white text-apn-blue hover:bg-gray-100">
              Join Groups
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Profile;
